import { useEffect, useMemo, useRef, useState } from "react";

import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import type { ScriptDetectionLogPayload } from "@frontierx/shared/schemas/clientMessageTypes";
import type { Call, LogMessage } from "@frontierx/shared/types";
import { Loader2, RefreshCw, Settings } from "lucide-react";
import type PartySocket from "partysocket";
import invariant from "tiny-invariant";
import { useSessionStorage } from "usehooks-ts";
import { useUser } from "../Auth/UnifiedAuthProvider";

import { useToast } from "@/components/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToastAction } from "@/components/ui/toast";

import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useAnalytics } from "../Analytics/AnalyticsProvider";
import { ScriptDetectionDebugger } from "../Debug/ScriptDetectionDebugger";
import { CallScriptSelector } from "../Script/CallScriptSelector";
import { SettingsManagerUI } from "../Settings/SettingsManagerUI";
import { CallDisplay } from "./CallDisplay";
import { CallList } from "./CallList/CallList";

declare const __APP_VERSION__: string;

export interface CallManagerUIProps {
  calls: Call[];
  selectedCall: string | null;
  error: string | null;
  logs: LogMessage[];
  scriptDetectionLogs: ScriptDetectionLogPayload[];
  isLoading?: boolean;
  createCall: (url: string, callScript: CallScript) => void;
  deleteCall: (roomId: string) => void;
  selectCall: (roomId: string) => void;
  leaveCall: () => void;
  clearLogs: () => void;
  clearScriptDetectionLogs: () => void;
  refreshCalls?: () => void;
  partySocket: PartySocket;
  mainSocket: PartySocket;
}

export function CallManagerUI({
  calls,
  selectedCall,
  error,
  logs,
  scriptDetectionLogs,
  isLoading = false,
  createCall,
  deleteCall,
  selectCall,
  leaveCall,
  clearLogs,
  clearScriptDetectionLogs,
  refreshCalls,
  partySocket,
  mainSocket,
}: Readonly<CallManagerUIProps>) {
  const { user } = useUser();
  const { trackEvent } = useAnalytics();
  const [showSettings, setShowSettings] = useState(false);
  const [url, setUrl] = useState("");
  const [isCreatingCall, setIsCreatingCall] = useState(false);
  const [selectedScript, setSelectedScript] = useState<CallScript | null>(null);
  const [currentCallSession, setCurrentCallSession] = useSessionStorage(
    "currentCallSession",
    "",
  );
  const hasRealtimeFlag = useFeatureFlag("realtime-api");
  const [isValidUrl, setIsValidUrl] = useState(false);
  const { toast } = useToast();
  const lastErrorRef = useRef<string | null>(null);
  const validateUrl = useMemo(() => {
    return (url: string) => {
      try {
        new URL(url.trim());
        return true;
      } catch {
        return false;
      }
    };
  }, []);

  useEffect(() => {
    setIsValidUrl(validateUrl(url));
  }, [url, validateUrl]);

  useEffect(() => {
    if (error && error !== lastErrorRef.current) {
      lastErrorRef.current = error;

      const mainSocketStatus = mainSocket?.readyState;
      let socketInfo = "";
      if (mainSocketStatus !== 1) {
        socketInfo += "Main connection disconnected. ";
      }

      let userFriendlyError = error;
      if (error.includes("Failed to fetch")) {
        userFriendlyError =
          "Unable to connect to the server. Please check your internet connection.";
      } else if (error.includes("WebSocket connection error")) {
        userFriendlyError =
          "Lost connection to the server. Some features may be unavailable.";
      }

      toast({
        variant: "destructive",
        title: "Error",
        description: `${socketInfo}${userFriendlyError}`,
        duration: 10000,
        action: (
          <ToastAction
            altText="Dismiss error"
            onClick={() => {
              lastErrorRef.current = null;
            }}
          >
            Dismiss
          </ToastAction>
        ),
      });
    }
  }, [error, toast, mainSocket]);

  useEffect(() => {
    if (!error) {
      lastErrorRef.current = null;
    }
  }, [error]);

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const leaveSettings = () => {
    setShowSettings(false);
  };

  useEffect(() => {
    if (selectedCall && selectedCall !== currentCallSession) {
      trackEvent("user_joined_call", {
        roomId: selectedCall,
        previousRoomId: currentCallSession,
      });
      setCurrentCallSession(selectedCall);
    }
  }, [selectedCall, currentCallSession, setCurrentCallSession, trackEvent]);

  const handleCreateCall = async () => {
    setIsCreatingCall(true);

    if (!isValidUrl) {
      setIsCreatingCall(false);
      return;
    }

    if (hasRealtimeFlag) {
      const defaultSDRScript: CallScript = {
        id: "default-realtime-script",
        title: "Default Realtime Script",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sections: [
          {
            title: "No Script",
            items: [
              "This call is using realtime mode without a predefined script.",
            ],
          },
        ],
      };

      try {
        createCall(url.trim(), selectedScript ?? defaultSDRScript);
        console.log("Call creation initiated");
      } catch (error) {
        console.error("Error creating call:", error);
        setIsCreatingCall(false);
        return;
      }
    } else {
      invariant(selectedScript, "Script is required for non-realtime calls");
      try {
        createCall(url.trim(), selectedScript);
        console.log("Call creation initiated");
      } catch (error) {
        console.error("Error creating call:", error);
        setIsCreatingCall(false);
        return;
      }
    }

    setTimeout(() => {
      setUrl("");
      setSelectedScript(null);
      setIsCreatingCall(false);
    }, 5000);
  };

  const handleRefresh = () => {
    if (refreshCalls) {
      refreshCalls();
    }
  };

  const sortedCalls = [...calls].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  return (
    <>
      {selectedCall === null && !showSettings && (
        <div className="flex h-full flex-col select-none">
          <div className="flex w-full items-center justify-end p-2">
            <div className="flex flex-row space-x-2">
              <div>
                <Button
                  onClick={toggleSettings}
                  variant="outline"
                  size="icon"
                  data-test="settings-button"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex h-full flex-grow flex-col space-y-4 overflow-hidden px-4">
            <div className="">
              <h2 className="mb-1 text-lg font-medium select-none">
                Start an AI-assisted call
              </h2>

              <p className="mb-3 text-sm text-gray-400 select-none">
                Enter a Google Meet, Teams or Zoom URL. The AI assistant will
                join.
              </p>

              <div className="flex w-full gap-4">
                <div className="flex-grow">
                  <Input
                    className="w-full px-2"
                    type="text"
                    value={url}
                    onChange={(e) => {
                      setUrl(e.target.value);
                    }}
                    placeholder="Your meeting URL: https://..."
                    disabled={isCreatingCall}
                    data-test="meeting-url-input"
                  />
                </div>
                <div>
                  <CallScriptSelector
                    mainSocket={mainSocket}
                    selectedScript={selectedScript}
                    onSelect={(callScript: CallScript) =>
                      setSelectedScript(callScript)
                    }
                  />
                </div>
                <Button
                  variant="default"
                  onClick={handleCreateCall}
                  disabled={
                    isCreatingCall ||
                    !isValidUrl ||
                    (!hasRealtimeFlag && !selectedScript)
                  }
                  data-test="start-call-button"
                  id="start-call-button"
                >
                  {isCreatingCall ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    "Start"
                  )}
                </Button>
              </div>
            </div>

            <div className="flex flex-grow flex-col space-y-3 overflow-hidden">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="mb-1 text-lg font-medium select-none">
                    Calls
                  </h2>
                  <p className="text-sm text-gray-400 select-none">
                    Calls started by anyone in your organisation are listed
                    here.
                  </p>
                </div>
                {refreshCalls && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isLoading}
                    className="h-8 px-2"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    <span className="ml-2 sr-only">Refresh</span>
                  </Button>
                )}
              </div>

              <div className="flex-grow overflow-hidden">
                {isLoading ? (
                  <div className="flex h-full items-center justify-center select-none">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Loading calls...</span>
                  </div>
                ) : (
                  <CallList
                    calls={sortedCalls}
                    selectCall={selectCall}
                    deleteCall={deleteCall}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex-0 flex-shrink-0 p-4">
            <p className="text-xs uppercase text-gray-400 select-none">
              Version: {__APP_VERSION__}
            </p>
          </div>
        </div>
      )}

      {user?.email?.endsWith("@frontierxai.com") && selectedCall === null && (
        <div className="p-4 border-t">
          <Tabs defaultValue="server-logs" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="server-logs">Server Logs</TabsTrigger>
              <TabsTrigger value="script-detection">
                Script Detection
              </TabsTrigger>
            </TabsList>

            <TabsContent value="server-logs" className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium select-none">Server Logs</h2>
                <Button variant="default" onClick={clearLogs}>
                  Clear Logs
                </Button>
              </div>
              <div
                id="logs"
                style={{
                  height: "200px",
                  overflowY: "scroll",
                  border: "1px solid #ccc",
                  padding: "10px",
                  marginTop: "10px",
                }}
              >
                {logs.map((log, index) => (
                  <div key={`log-${log.timestamp}-${index}`}>
                    <small>
                      [{new Date(log.timestamp).toLocaleTimeString()}]
                    </small>{" "}
                    {log.message}
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="script-detection">
              <ScriptDetectionDebugger
                logs={scriptDetectionLogs}
                onClearLogs={clearScriptDetectionLogs}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}

      {selectedCall && !showSettings && (
        <CallDisplay
          mainSocket={mainSocket}
          roomId={selectedCall}
          leaveCall={leaveCall}
        />
      )}

      {showSettings && (
        <SettingsManagerUI partySocket={partySocket} onClose={leaveSettings} />
      )}
    </>
  );
}
